/**
 * Main Application Component
 * 
 * Root component for the Supply Chain Tension Modeler
 * Requirements: 9.1, 9.2
 */

import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { getCurrentUser, signOut, fetchAuthSession } from 'aws-amplify/auth';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import AnalysisList from './pages/AnalysisList';
import UserManagement from './pages/UserManagement';
import { AnalysisEditor } from './components/editor/AnalysisEditor';

// Configure Amplify (these values should come from environment variables in production)
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_USER_POOL_ID || '',
      userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID || '',
      identityPoolId: import.meta.env.VITE_IDENTITY_POOL_ID || '',
    }
  },
  API: {
    REST: {
      SupplyChainAPI: {
        endpoint: import.meta.env.VITE_API_ENDPOINT || '',
        region: import.meta.env.VITE_AWS_REGION || 'us-west-1',
      }
    }
  }
});

interface User {
  username: string;
  userId: string;
  email?: string;
}

/**
 * Protected Route Component
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      await getCurrentUser();
      setIsAuthenticated(true);
    } catch {
      setIsAuthenticated(false);
    }
  };

  if (isAuthenticated === null) {
    return <div>Loading...</div>;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

/**
 * Login Page Component with Auto-Redirect
 */
const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuthAndRedirect();
  }, []);

  const checkAuthAndRedirect = async () => {
    try {
      await getCurrentUser();
      setIsAuthenticated(true);
      navigate('/analyses');
    } catch {
      setIsAuthenticated(false);
    }
  };

  if (isAuthenticated) {
    return <Navigate to="/analyses" />;
  }

  return (
    <div className="login-page">
      <h2>Login to Supply Chain Tension Modeler</h2>
      <Authenticator
        signUpAttributes={['email']}
      >
        {({ user }) => {
          // Auto-redirect after successful authentication
          if (user) {
            setTimeout(() => navigate('/analyses'), 100);
          }
          return (
            <div>
              <p>Welcome {user?.username}! Redirecting...</p>
            </div>
          );
        }}
      </Authenticator>
    </div>
  );
};

/**
 * Main App Component
 */
function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

/**
 * App Content Component (inside Router context)
 */
function AppContent() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    checkUser();
  }, [location.pathname]); // Re-check user when route changes

  const checkUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      setUser({
        username: currentUser.username,
        userId: currentUser.userId,
        email: session.tokens?.idToken?.payload.email as string
      });
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div className="app-loading">
        <h2>Loading...</h2>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <h1>Supply Chain Tension Modeler</h1>
          <nav className="header-nav">
            <Link to="/">Home</Link>
            <Link to="/analyses">Analyses</Link>
            {user && <Link to="/admin/users">User Management</Link>}
            {user ? (
              <div className="user-menu">
                <span>Welcome, {user.username}</span>
                <button onClick={handleSignOut}>Sign Out</button>
              </div>
            ) : (
              <Link to="/login">Login</Link>
            )}
          </nav>
        </div>
      </header>

      <div className="app-layout">
        <main className="app-main">
          <Routes>
            <Route path="/" element={
              <div className="home-page">
                <h2>Welcome to Supply Chain Tension Modeler</h2>
                <p>Analyze and visualize regulatory tensions in your supply chain.</p>
                <Link to="/analyses" className="cta-button">Get Started</Link>
              </div>
            } />
            
            <Route path="/analyses" element={
              <ProtectedRoute>
                <AnalysisList />
              </ProtectedRoute>
            } />
            
            <Route path="/analyses/:id" element={
              <ProtectedRoute>
                <AnalysisEditorWrapper />
              </ProtectedRoute>
            } />
            
            <Route path="/admin/users" element={
              <ProtectedRoute>
                <UserManagement />
              </ProtectedRoute>
            } />
            
            <Route path="/login" element={<LoginPage />} />
            
            <Route path="*" element={
              <div className="not-found">
                <h2>404 - Page Not Found</h2>
                <Link to="/">Go Home</Link>
              </div>
            } />
          </Routes>
        </main>
      </div>

      <footer className="app-footer">
        <p>Supply Chain Tension Modeler © 2026</p>
      </footer>
    </div>
  );
}

// Wrapper component to get route params
function AnalysisEditorWrapper() {
  const { id } = useParams<{ id: string }>();
  return <AnalysisEditor analysisId={id || ''} onSave={() => {}} />;
}

export default App;
